import {
  CreateParentForm,
  UpdateExistingParentContent,
  useApplicationAddExistingSteps,
} from '@schooly/components/applications';
import { useCustomFields } from '@schooly/hooks/use-custom-fields';
import { Loading, ModalSmall } from '@schooly/style';
import { useCallback, useMemo } from 'react';

import { useApplication } from '../../../../context/applications/useApplication';
import { SelectParentContent } from './SelectParentContent';

type AddParentModalProps = {
  onClose: () => void;
  addedApplicationParents: CreateParentForm[];
  setApplicationParents: (p: CreateParentForm[]) => void;
};

export const AddParentModal: React.FC<AddParentModalProps> = ({
  onClose,
  addedApplicationParents,
  setApplicationParents,
}) => {
  const { pendingParents, setPendingParents } = useApplication();
  const { applicationAdultCustomFields, fetching: isLoadingCustomFields } = useCustomFields({
    refetchOnMount: 'always',
  });

  const addedParentProfileIds = useMemo(() => {
    return addedApplicationParents.reduce<string[]>(
      (acc, p) => (p.profile_id ? [...acc, p.profile_id] : acc),
      [],
    );
  }, [addedApplicationParents]);

  const newParents = useMemo(() => {
    return pendingParents.filter(
      (adult) => adult.profile_id && !addedParentProfileIds.includes(adult.profile_id),
    );
  }, [addedParentProfileIds, pendingParents]);

  const onCompleteParents = useCallback(
    (parents: CreateParentForm[]) => {
      setApplicationParents(parents);
      onClose();
    },
    [setApplicationParents, onClose],
  );

  const updatePendingParent = useCallback(
    (parent: CreateParentForm) => {
      setPendingParents((prev) => {
        return prev.map((p) => (p.id === parent.id ? parent : p));
      });
    },
    [setPendingParents],
  );

  const removePendingParent = useCallback(
    (parentId: string) => {
      setPendingParents((prev) => prev.filter((p) => p.id !== parentId));
    },
    [setPendingParents],
  );

  const {
    step,
    currentEntity: currentParent,
    onConfirmSelection,
    onUpdateEntity,
    onCloseEntityUpdate,
  } = useApplicationAddExistingSteps<CreateParentForm>({
    newEntities: newParents,
    allEntities: pendingParents,
    customFields: applicationAdultCustomFields ?? [],
    onComplete: onCompleteParents,
    updateEntity: updatePendingParent,
    removeEntity: removePendingParent,
  });

  const onCloseParentUpdate = useCallback(() => {
    if (!currentParent) return;
    onCloseEntityUpdate(currentParent.id);
  }, [currentParent, onCloseEntityUpdate]);

  const renderContent = () => {
    if (isLoadingCustomFields) {
      return <Loading />;
    }

    if (step === 'select') {
      return (
        <SelectParentContent
          onClose={onClose}
          onConfirm={onConfirmSelection}
          onSelectAdults={setPendingParents}
          addedAdultsIds={addedParentProfileIds}
          selectedAdults={pendingParents}
        />
      );
    }

    if (!currentParent) return null;

    return (
      <UpdateExistingParentContent
        onClose={onCloseParentUpdate}
        onSubmit={onUpdateEntity}
        parent={currentParent}
      />
    );
  };

  return (
    <ModalSmall onClose={step === 'select' ? onClose : onCloseParentUpdate} open>
      {renderContent()}
    </ModalSmall>
  );
};
